import React from "react";
import styled from "styled-components";
import T from "prop-types";
import Slider from "react-slick";
import { InView } from "react-intersection-observer";
import { SlickStyles } from "@nested/ui";

import { media } from "../../media";
import { NextArrow, PrevArrow } from "./Arrows";
import { PropositionSlide } from "./PropositionSlide";
import { desktopImageSizing } from "./utils";

const PhotoCarouselContainer = styled.div`
  position: relative;
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  ${media.desktop`
    ${desktopImageSizing()}
    margin-left: 35px;
    margin-right: 0;
  `};
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vw / (16 / 9));
  background-color: ${({ theme }) => theme.palette.sand40};
  ${media.tablet`
    background-color: ${({ theme }) => theme.palette.hague130};
    height: 400px;
    cursor: grabbing;
  `};
  ${media.desktop`
    ${desktopImageSizing()}
  `};
`;

export const Pagination = styled.div`
  position: absolute;
  text-align: left;
  margin-bottom: 0px;
  bottom: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
`;

export const PaginationText = styled.h6`
  position: absolute;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 2px;
  bottom: 0;
  left: 15px;
  line-height: 1;
  color: white;
  width: 100px;
`;

const Img = styled.img`
  width: 100%;
  object-fit: cover;
  height: calc(100vw / (16 / 9));

  ${media.tablet`
    height: 400px;
    object-fit: contain;
  `};
  ${media.desktop`
    ${desktopImageSizing()}
  `};
`;

class PhotoCarousel extends React.Component {
  defaultSettings = {
    afterChange: (newIndex) => this.setState({ currentIndex: newIndex + 1 }),
    nextArrow: <NextArrow colour="blue" />,
    prevArrow: <PrevArrow colour="blue" />,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentIndex: 1,
    };
  }

  /*
   * this is all needed to prevent vertical scrolling on iOS while horizontally swiping
   * see: https://github.com/akiran/react-slick/issues/1240#issuecomment-396484553
   */
  componentDidMount() {
    window.addEventListener("touchstart", this.touchStart);
    window.addEventListener("touchmove", this.preventTouch, { passive: false });
  }

  componentWillUnmount() {
    window.removeEventListener("touchstart", this.touchStart);
    window.removeEventListener("touchmove", this.preventTouch, {
      passive: false,
    });
  }

  touchStart(e) {
    this.firstClientX = e.touches[0].clientX;
    this.firstClientY = e.touches[0].clientY;
  }

  preventTouch(e) {
    const minValue = 5; // threshold

    this.clientX = e.touches[0].clientX - this.firstClientX;
    this.clientY = e.touches[0].clientY - this.firstClientY;

    // disable vertical scrolling
    if (Math.abs(this.clientX) > minValue) {
      e.preventDefault();
      e.returnValue = false;
      return false;
    }
    return true;
  }

  render() {
    const { photos, showPropositionSlide = true } = this.props;
    const { currentIndex } = this.state;

    return (
      <PhotoCarouselContainer className="chromatic-ignore">
        <CarouselContainer>
          <SlickStyles />
          <InView triggerOnce>
            {({ ref }) => (
              <div ref={ref}>
                <Slider {...this.defaultSettings}>
                  {photos.map((photoUrl, index) => (
                    <div key={`${photoUrl + index}`} style={{ width: "100%" }}>
                      <Img src={photoUrl} alt="" />
                    </div>
                  ))}
                  {showPropositionSlide && <PropositionSlide />}
                </Slider>
              </div>
            )}
          </InView>
          <Pagination>
            <PaginationText>
              {
                // Number(true) = 1
                // Number(false) = 0
                // https://www.youtube.com/watch?v=et8xNAc2ic8 because javascript so this works
                `${currentIndex} OF ${
                  photos.length + Number(showPropositionSlide)
                }`
              }
            </PaginationText>
          </Pagination>
        </CarouselContainer>
      </PhotoCarouselContainer>
    );
  }
}

PhotoCarousel.propTypes = {
  photos: T.array,
};

export { PhotoCarousel };
